import { ReactElement, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { keyBy, merge } from 'lodash-es'
import { Stack } from '@mui/material'
import type { DeleteProjectConnectionResultDTO } from '@api/fastAPI.schemas'
import {
  getGetConnectionUsageQueryKey,
  useGetConnectionUsage,
} from '@api/project-connection-usage/project-connection-usage'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useSystemManagerStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getProjectConnectionPrioritization, getProjectConnections } from '@queries'
import { deletedConnectorMessage } from 'src/components/manager/connections/utils/deleteConfirmation'
import { buildErrorMessage } from 'src/constants/errors'
import {
  createProjectConnection,
  deleteProjectConnection,
  editProjectConnection,
  saveProjectPrioritization,
} from 'src/state/mutations/connections'
import ConnectionListWithDeleteCheck from './components/ConnectionListWithDeleteCheck'
import PrioritizationWarning from './components/PrioritizationWarning'
import ProjectConnectionPrioritizationLists from './components/ProjectConnectionPrioritizationLists'
import { schema } from './schema'

const ConnectionManagerForm = (): ReactElement => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const connections = useSystemManagerStore(state => state.connections)
  const connectionPrioritization = useSystemManagerStore(state => state.prioritization)
  const invalidateResults = useResultsInvalidation()

  const { enqueueSnackbar } = useSnackbar()

  // How many anchors use each Zugverbinder, for the table and the delete confirmation.
  const { data: connectionUsage } = useGetConnectionUsage(projectId as string, {
    query: {
      enabled: !!projectId,
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Verwendung der Zuganker'),
          { variant: 'error' },
        ),
    },
  })
  const usageByGuid = useMemo(
    () => keyBy(connectionUsage ?? [], usage => usage.connectionGuid),
    [connectionUsage],
  )

  const defaultValues = useMemo<ProjectConnectionPrioritization>(() => {
    const schemaDefault = schema.getDefault()
    return merge(schemaDefault, connectionPrioritization)
  }, [connectionPrioritization])

  const { mutate: onSave } = useMutation(
    (connectionPrioritization: ProjectConnectionPrioritization) =>
      saveProjectPrioritization.request({ projectId, input: connectionPrioritization }),
    {
      onSuccess: async () => {
        enqueueSnackbar('Zugverbindungspriorisierung erfolgreich gespeichert', {
          variant: 'success',
        })

        await queryClient.invalidateQueries(getProjectConnectionPrioritization.getKey(projectId))

        invalidateResults(projectId as string)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindungspriorisierung'),
          {
            variant: 'error',
          },
        )
      },
    },
  )

  const handlePrioritizationChange = useCallback(
    (values: ProjectConnectionPrioritization) => {
      onSave(values)
    },
    [onSave],
  )

  const { mutate: onDeleteConnection } = useMutation(
    (connectionId: string) => deleteProjectConnection.request(projectId as string, connectionId),
    {
      onSuccess: async (result: DeleteProjectConnectionResultDTO) => {
        await queryClient.invalidateQueries(getProjectConnections.getKey(projectId))
        await queryClient.invalidateQueries(getGetConnectionUsageQueryKey(projectId as string))
        invalidateResults(projectId as string)

        const resetCount = result?.resetUserDefinedAnchorCount ?? 0
        enqueueSnackbar(deletedConnectorMessage(resetCount, 'tensile'), { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Zugverbindungen'), {
          variant: 'error',
        })
      },
    },
  )

  const editConnectionMutationResult = useMutation(
    (data: ProjectConnection) => editProjectConnection.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectConnections.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Zugverbindung erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindung'), {
          variant: 'error',
        })
      },
    },
  )

  const createConnectionMutationResult = useMutation(
    (data: ProjectConnection) => createProjectConnection.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectConnections.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Zugverbindung erfolgreich angelegt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindung'), {
          variant: 'error',
        })
      },
    },
  )

  return (
    <Form
      id="project-system-manager-connections"
      onSubmit={() => undefined}
      validationSchema={schema}
      defaultValues={defaultValues}
    >
      <Stack direction="column" spacing={5}>
        <ConnectionListWithDeleteCheck
          connections={connections}
          onDeleteConnection={onDeleteConnection}
          usageByGuid={usageByGuid}
          editMutation={editConnectionMutationResult}
          createMutation={createConnectionMutationResult}
          defaultRowsPerPage={5}
        />

        <PrioritizationWarning />

        <ProjectConnectionPrioritizationLists
          connections={connections}
          onSave={handlePrioritizationChange}
        />
      </Stack>
    </Form>
  )
}

export default ConnectionManagerForm
